import React, { useState, useMemo, useCallback } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays } from 'date-fns';
import { enGB } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  useGetHolidayRequestsQuery,
  useGetUserProfilesQuery,
  HOLIDAY_FETCH_PAGE_SIZE,
} from '../../store/slices/apiSlice';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import type { HolidayRequest, UserProfile } from '../../utils/types';
import { inclusiveDateRangeToCalendarEvent } from '../../utils/holidayCalendarEvents';

// Setup date-fns localizer
const locales = {
  'en-GB': enGB,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// View labels for the toolbar
const VIEW_LABELS: Record<string, string> = {
  month: 'Month',
  week: 'Week',
  day: 'Day',
  agenda: 'Agenda',
};

type CalendarViewType = 'month' | 'week' | 'day' | 'agenda';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: {
    userId: number;
    userName: string;
    reason: string;
    totalDays: number;
    color?: string;
  };
}

export const HolidayCalendarPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarViewType>('month');

  // Calculate date range for fetching holidays based on current view
  const dateRange = useMemo(() => {
    if (view === 'month') {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate),
      };
    } else if (view === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday start
      return {
        start: weekStart,
        end: addDays(weekStart, 6),
      };
    } else if (view === 'day') {
      return {
        start: currentDate,
        end: currentDate,
      };
    } else {
      // Agenda - show a month range
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate),
      };
    }
  }, [currentDate, view]);

  // Fetch approved holiday requests for the visible date range
  const { data: requestsData, isLoading: isLoadingRequests } = useGetHolidayRequestsQuery({
    year: currentDate.getFullYear(),
    status: 'approved',
    page_size: HOLIDAY_FETCH_PAGE_SIZE,
  });

  // Fetch users to get their colors
  const { data: usersData, isLoading: isLoadingUsers } = useGetUserProfilesQuery({
    page_size: 1000,
  });

  // Create user color map
  const userColorMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (usersData?.results) {
      (usersData.results as UserProfile[]).forEach((user) => {
        if (user.assigned_color_data) {
          map[user.user] = user.assigned_color_data.hex_value;
        }
      });
    }
    return map;
  }, [usersData]);

  // Transform holiday requests to calendar events
  const events = useMemo(() => {
    if (!requestsData?.results) return [];

    return (requestsData.results as HolidayRequest[])
      .filter((request) => request.status === 'approved')
      .map((request) => {
        const userColor = userColorMap[request.user];
        const { start, end, allDay } = inclusiveDateRangeToCalendarEvent(
          request.start_date,
          request.end_date
        );

        return {
          id: request.id,
          title: `${request.user_details?.first_name} ${request.user_details?.last_name}`,
          start,
          end,
          allDay,
          resource: {
            userId: request.user,
            userName: `${request.user_details?.first_name} ${request.user_details?.last_name}`,
            reason: request.reason,
            totalDays: request.total_days,
            color: userColor,
          },
        };
      });
  }, [requestsData, userColorMap]);

  // Filter events to visible date range
  const visibleEvents = useMemo(() => {
    const rangeStart = dateRange.start.getTime();
    const rangeEnd = dateRange.end.getTime();
    
    return events.filter((event) => {
      const eventStart = event.start.getTime();
      const eventEnd = event.end.getTime();
      // Event overlaps with visible range if it starts before range end and ends after range start
      return eventStart <= rangeEnd && eventEnd >= rangeStart;
    });
  }, [events, dateRange]);

  // Navigation handlers
  const handleNavigate = useCallback((action: 'PREV' | 'NEXT' | 'TODAY') => {
    switch (action) {
      case 'PREV':
        if (view === 'month') {
          setCurrentDate(prev => subMonths(prev, 1));
        } else if (view === 'week') {
          setCurrentDate(prev => subWeeks(prev, 1));
        } else {
          setCurrentDate(prev => subDays(prev, 1));
        }
        break;
      case 'NEXT':
        if (view === 'month') {
          setCurrentDate(prev => addMonths(prev, 1));
        } else if (view === 'week') {
          setCurrentDate(prev => addWeeks(prev, 1));
        } else {
          setCurrentDate(prev => addDays(prev, 1));
        }
        break;
      case 'TODAY':
        setCurrentDate(new Date());
        break;
    }
  }, [view]);

  // View change handler
  const handleViewChange = useCallback((newView: CalendarViewType) => {
    setView(newView);
  }, []);

  // Format header date based on view
  const formatHeaderDate = useCallback((date: Date, currentView: string) => {
    switch (currentView) {
      case 'month':
        return format(date, 'MMMM yyyy');
      case 'week':
        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 6);
        if (weekStart.getMonth() === weekEnd.getMonth()) {
          return format(weekStart, 'MMMM yyyy');
        } else if (weekStart.getFullYear() === weekEnd.getFullYear()) {
          return `${format(weekStart, 'MMMM')} - ${format(weekEnd, 'MMMM yyyy')}`;
        } else {
          return `${format(weekStart, 'MMM yyyy')} - ${format(weekEnd, 'MMM yyyy')}`;
        }
      case 'day':
        return format(date, 'EEEE, MMMM d, yyyy');
      default:
        return format(date, 'MMMM yyyy');
    }
  }, []);

  // Custom toolbar component
  const CustomToolbar = useCallback(() => {
    const views: CalendarViewType[] = ['month', 'week', 'day'];
    
    return (
      <div className="bg-white border-b border-gray-200 px-4 py-3 mb-4 rounded-t-lg">
        {/* Single Row: Navigation, Date Display, and View Switcher */}
        <div className="flex items-center justify-between">
          {/* Navigation Controls (Left) */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleNavigate('PREV')}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            
            <button
              onClick={() => handleNavigate('TODAY')}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors font-medium text-sm"
            >
              Today
            </button>
            
            <button
              onClick={() => handleNavigate('NEXT')}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Current Date Display (Center) */}
          <h2 className="text-xl font-semibold text-gray-900">
            {formatHeaderDate(currentDate, view)}
          </h2>

          {/* View Switcher (Right) */}
          <div className="flex space-x-1">
            {views.map((v) => (
              <button
                key={v}
                onClick={() => handleViewChange(v)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  view === v
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {VIEW_LABELS[v]}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }, [currentDate, view, handleNavigate, handleViewChange, formatHeaderDate]);

  // Custom event component
  const EventComponent = ({ event }: { event: CalendarEvent }) => {
    const backgroundColor = event.resource.color || '#3b82f6';
    
    return (
      <div
        className="h-full w-full rounded px-1 py-0.5 text-sm font-medium text-white overflow-hidden flex items-center"
        style={{ backgroundColor }}
        title={`${event.resource.userName}: ${event.resource.reason} (${event.resource.totalDays} days)`}
      >
        <span className="truncate">{event.resource.userName}</span>
      </div>
    );
  };

  const isLoading = isLoadingRequests || isLoadingUsers;

  return (
    <AppShell>
      <PageLayout
        title="Holiday Calendar"
        subtitle="View approved holidays for all team members"
        bgColor="bg-purple-50"
        textColor="text-purple-900"
        borderColor="border-purple-200"
        icon={<CalendarIcon className="w-6 h-6" />}
      >
        <style>{`
          .holiday-calendar .rbc-month-view {
            height: auto !important;
            min-height: 800px;
          }
          .holiday-calendar .rbc-month-row {
            min-height: 140px !important;
            height: 140px !important;
          }
          .holiday-calendar .rbc-row-content {
            min-height: auto !important;
          }
          .holiday-calendar .rbc-event {
            padding: 1px 3px !important;
            font-size: 13px !important;
            line-height: 1.4 !important;
            min-height: 22px !important;
          }
          .holiday-calendar .rbc-event-content {
            font-size: 13px !important;
            line-height: 1.4 !important;
          }
          .holiday-calendar .rbc-date-cell {
            padding: 2px 4px !important;
            font-size: 12px !important;
          }
          .holiday-calendar .rbc-toolbar {
            display: none !important;
          }
          /* Today highlighting */
          .holiday-calendar .rbc-today {
            background-color: rgba(147, 51, 234, 0.08) !important;
          }
        `}</style>
        <div className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="flex justify-center items-center h-96">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="holiday-calendar p-6">
              {/* Custom Toolbar */}
              <CustomToolbar />
              
              {/* Calendar */}
              <Calendar
                localizer={localizer}
                events={visibleEvents}
                startAccessor="start"
                endAccessor="end"
                allDayAccessor="allDay"
                date={currentDate}
                onNavigate={setCurrentDate}
                view={view as 'month' | 'week' | 'day'}
                onView={(newView) => setView(newView as CalendarViewType)}
                components={{
                  event: EventComponent,
                }}
                eventPropGetter={() => ({
                  style: {
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '1px',
                  },
                })}
                views={['month', 'week', 'day']}
                defaultView="month"
                popup
                tooltipAccessor={(event: CalendarEvent) =>
                  `${event.resource.userName}: ${event.resource.reason} (${event.resource.totalDays} days)`
                }
              />
            </div>
          )}

          {/* Legend */}
          {!isLoading && events.length > 0 && (
            <div className="px-6 pb-6">
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Team Members</h4>
                <div className="flex flex-wrap gap-3">
                  {Array.from(new Set(events.map((e) => e.resource.userId))).map((userId) => {
                    const event = events.find((e) => e.resource.userId === userId);
                    if (!event) return null;
                    return (
                      <div key={userId} className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: event.resource.color || '#3b82f6' }}
                        />
                        <span className="text-sm text-gray-600">{event.resource.userName}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </PageLayout>
    </AppShell>
  );
};

export default HolidayCalendarPage;
